import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { MediaObject } from 'types'
import { isVideo } from 'components/ImagePickerModal/utils'
import { useRootStore } from 'stores/RootStore'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import { GeneratingOverlay } from '../GeneratingOverlay'
import Styles from './ReelsVideoPlayer.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface ReelsVideoPlayerProps {
  mediaObjects?: MediaObject[]
  showVideoControls?: boolean
  isActive?: boolean
  className?: string
  dataTestId?: string
  username?: string
  isGenerating?: boolean
  videoRef?: React.RefObject<HTMLVideoElement>
  almanacFileKey: string
}

export const ReelsVideoPlayer = observer(
  ({
    mediaObjects,
    showVideoControls,
    isActive,
    className,
    dataTestId,
    username,
    videoRef,
    almanacFileKey,
    isGenerating = false,
  }: ReelsVideoPlayerProps) => {
    const { generationTimestampStore } = useRootStore()
    const isRefineAiGenerationEnabled = useFlipperFlag('refine_ai_generation')

    const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })
    const videoUrl = video ? mediaObjects[0].source_url : undefined
    const image = mediaObjects?.length && !video ? mediaObjects[0].source_url : undefined

    const isDesignRegeneration = !!generationTimestampStore.getStartedAt(almanacFileKey) && isRefineAiGenerationEnabled
    const showVideoPlayerLoading = isGenerating && !isDesignRegeneration
    const showGeneratingOverlay = isGenerating && isDesignRegeneration

    if (!mediaObjects?.length) {
      return null
    }

    if (video && showVideoControls) {
      return (
        <div className={Styles.container}>
          <VideoPlayer
            mediaObject={mediaObjects[0]}
            className={cx(className, { [Styles.blurred]: showGeneratingOverlay })}
            data-testid={dataTestId}
            isActive={isActive && !isGenerating}
            videoRef={videoRef}
            almanacFileKey={almanacFileKey}
            isGenerating={showVideoPlayerLoading}
          />
          <GeneratingOverlay isVisible={showGeneratingOverlay} designKey={almanacFileKey} />
        </div>
      )
    }

    if (video) {
      return (
        <div className={Styles.container}>
          <video
            ref={videoRef}
            src={videoUrl}
            className={cx(className, { [Styles.blurred]: showGeneratingOverlay })}
            autoPlay
            loop
            muted
            playsInline
            data-testid={dataTestId}
          />
          <GeneratingOverlay isVisible={showGeneratingOverlay} designKey={almanacFileKey} />
        </div>
      )
    }

    if (image) {
      return (
        <div className={Styles.container}>
          <img src={image} alt={username} className={cx(className, { [Styles.blurred]: showGeneratingOverlay })} />
          <GeneratingOverlay isVisible={showGeneratingOverlay} designKey={almanacFileKey} />
        </div>
      )
    }

    return null
  },
)
